import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "hooks/usePermissions";
import { Box, CircularProgress, Typography } from "@mui/material";
import { checkPermission, checkPermissionsBatch } from "services/permission.service";
import { getToken } from "services/auth.service";

const EMPTY_PERMISSIONS = [];

// Component để bảo vệ route dựa trên quyền
const PermissionGuard = ({
  children,
  requirePermission = null,
  requireAnyPermissions = EMPTY_PERMISSIONS,
  fallback = null,
  redirectTo = "/unauthorized",
}) => {
  const { loading, isAdmin } = usePermissions();
  const [hasPermission, setHasPermission] = useState(null); // null = chưa kiểm tra
  const [permissionLoading, setPermissionLoading] = useState(false);
  const checkedRef = useRef(false); // Flag để tránh kiểm tra nhiều lần

  useEffect(() => {
    const checkUserPermission = async () => {
      // Kiểm tra authentication trước
      const token = getToken();
      if (!token) {
        return;
      }

      // Nếu đang loading thông tin user, chờ
      if (loading) return;

      // Tránh kiểm tra nhiều lần cho cùng một permission và user state
      const currentPermission = requireAnyPermissions.length > 0
        ? `any:${requireAnyPermissions.join("|")}`
        : requirePermission;
      const currentIsAdmin = isAdmin();
      if (
        checkedRef.current &&
        checkedRef.current.permission === currentPermission &&
        checkedRef.current.isAdmin === currentIsAdmin
      ) {
        return;
      }

      // Nếu là Admin, luôn có quyền
      if (currentIsAdmin) {
        setHasPermission(true);
        checkedRef.current = {
          permission: currentPermission,
          isAdmin: currentIsAdmin,
        };
        return;
      }

      // Nếu không yêu cầu quyền gì, có quyền
      if (!requirePermission && requireAnyPermissions.length === 0) {
        setHasPermission(true);
        checkedRef.current = {
          permission: currentPermission,
          isAdmin: currentIsAdmin,
        };
        return;
      }

      // Kiểm tra quyền bất đồng bộ
      setPermissionLoading(true);
      try {
        const allowed = requireAnyPermissions.length > 0
          ? Object.values(
              (await checkPermissionsBatch(requireAnyPermissions)).results || {}
            ).some(Boolean)
          : (await checkPermission(requirePermission)).hasPermission;
        setHasPermission(allowed);
        checkedRef.current = {
          permission: currentPermission,
          isAdmin: currentIsAdmin,
        };
      } catch (error) {
        console.error("PermissionGuard: Error checking permission:", error);
        setHasPermission(false);
        checkedRef.current = {
          permission: currentPermission,
          isAdmin: currentIsAdmin,
        };
      } finally {
        setPermissionLoading(false);
      }
    };

    checkUserPermission();
  }, [loading, requirePermission, requireAnyPermissions, isAdmin]); // Thêm lại isAdmin để tránh warning

  // Reset checked flag khi permission thay đổi
  useEffect(() => {
    checkedRef.current = null;
    setHasPermission(null);
  }, [requirePermission, requireAnyPermissions]);

  // Kiểm tra authentication trước khi hiển thị gì cả
  const token = getToken();
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Hiển thị loading khi đang tải thông tin user hoặc kiểm tra quyền
  if (loading || permissionLoading || hasPermission === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Đang kiểm tra quyền truy cập...
        </Typography>
      </Box>
    );
  }

  // Nếu không có quyền
  if (!hasPermission) {
    if (fallback) {
      return fallback;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Có quyền thì render children
  return children;
};

export default PermissionGuard;
